import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Moon, Sun, Sparkles, LogOut, LayoutDashboard } from "lucide-react";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { useTheme } from "../lib/theme-context";
import { useAuth } from "../lib/auth-context";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/contact", label: "Contact" },
];

export default function MarketingNavbar() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="marketing-navbar"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-background/70 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 h-16">
        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center gap-2 font-display text-lg font-semibold"
        >
          <span className="grid place-items-center w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-pink-500 shadow-lg shadow-cyan-500/20">
            <Sparkles className="w-4 h-4 text-zinc-950" />
          </span>
          <span>
            TikBoost <span className="text-cyan-400">AI</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `transition-colors duration-200 hover:text-foreground ${
                  isActive ? "text-foreground" : "text-muted-foreground"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            data-testid="theme-toggle"
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>

          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <Button asChild variant="ghost" size="sm" data-testid="nav-dashboard">
                <Link to={user.role === "admin" ? "/admin" : "/dashboard"}>
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                </Link>
              </Button>
              <Button size="sm" variant="secondary" onClick={handleLogout} data-testid="nav-logout">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Button asChild size="sm" variant="ghost" data-testid="nav-login">
                <Link to="/login">Log in</Link>
              </Button>
              <Button
                asChild
                size="sm"
                className="rounded-full bg-gradient-to-r from-cyan-400 to-pink-500 text-zinc-950 hover:brightness-110 border-0"
                data-testid="nav-register"
              >
                <Link to="/register">Get started</Link>
              </Button>
            </div>
          )}

          {/* Mobile */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" data-testid="nav-mobile-toggle">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background border-l border-border">
              <div className="mt-8 flex flex-col gap-4">
                {navLinks.map((l) => (
                  <Link key={l.to} to={l.to} className="text-lg" data-testid={`m-nav-${l.label.toLowerCase()}`}>
                    {l.label}
                  </Link>
                ))}
                <div className="h-px bg-border my-2" />
                {user ? (
                  <>
                    <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="text-lg" data-testid="m-nav-dashboard">
                      Dashboard
                    </Link>
                    <button onClick={handleLogout} className="text-lg text-left" data-testid="m-nav-logout">
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="text-lg" data-testid="m-nav-login">Log in</Link>
                    <Link to="/register" className="text-lg" data-testid="m-nav-register">Get started</Link>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
